import React, { useState, useEffect, useRef, useMemo, useDeferredValue } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../../db/database';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

// ─── Helpers ──────────────────────────────────────────────────────────

export function normaliseSearchText(text) {
  if (!text) return '';
  return String(text)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\w\s@.+]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function normalisePhoneDigits(phone) {
  if (!phone) return '';
  return String(phone).replace(/\D/g, '');
}

export function parseSearchAmount(str) {
  const match = String(str).match(/[\d,.]+/);
  if (!match) return null;
  const val = parseFloat(match[0].replace(/,/g, ''));
  return isNaN(val) ? null : val;
}

export function normaliseCurrencyText(currency) {
  return String(currency || 'AUD').toUpperCase();
}

export function subsequenceScore(query, text) {
  if (!query || !text) return 0;
  let qIdx = 0;
  for (let i = 0; i < text.length && qIdx < query.length; i++) {
    if (text[i] === query[qIdx]) qIdx++;
  }
  return qIdx === query.length ? 1 : (qIdx / query.length) * 0.5;
}

export function boundedDamerauLevenshtein(a, b, maxDistance = 3) {
  if (a === b) return 0;
  if (a.length > b.length) [a, b] = [b, a];
  if (b.length - a.length > maxDistance) return maxDistance + 1;
  let v0 = new Array(b.length + 1);
  let v1 = new Array(b.length + 1);
  for (let i = 0; i <= b.length; i++) v0[i] = i;
  for (let i = 0; i < a.length; i++) {
    v1[0] = i + 1;
    let minVal = v1[0];
    for (let j = 0; j < b.length; j++) {
      const cost = a[i] === b[j] ? 0 : 1;
      v1[j + 1] = Math.min(v1[j] + 1, v0[j + 1] + 1, v0[j] + cost);
      minVal = Math.min(minVal, v1[j + 1]);
    }
    if (minVal > maxDistance) return maxDistance + 1;
    for (let j = 0; j <= b.length; j++) v0[j] = v1[j];
  }
  return v0[b.length];
}

// ─── Metadata Extraction ──────────────────────────────────────────────

export function getProposalSearchMetadata(proposal, settings) {
  const items = proposal.financials?.items || [];
  const subtotal = items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  const taxEnabled = proposal.financials?.taxEnabled === true;
  const taxRate = typeof settings?.taxRate === 'number' ? settings.taxRate : 10;
  const taxAmount = taxEnabled ? subtotal * (taxRate / 100) : 0;
  const total = subtotal + taxAmount;

  const milestones = proposal.paymentMilestones || [];
  const depositMilestone = milestones.find((m) => m.label && String(m.label).toLowerCase().includes('deposit'));
  const depositPercent = depositMilestone ? Number(depositMilestone.percent) || 0 : 0;
  const depositAmount = total * (depositPercent / 100);

  const isPub = proposal.status === 'Published' || proposal.status === 'Sent';
  const publicationStatus = isPub ? 'Published' : 'Not Published';
  const approvalStatus = proposal.approval?.status || 'NOT_SENT';

  const approvalDetailMap = {
    PENDING: 'Awaiting Approval',
    APPROVED: 'Approved',
    CHANGES_REQUESTED: 'Changes Requested',
    EXPIRED: 'Expired',
    REVOKED: 'Revoked',
    SEND_FAILED: 'Send Failed',
    NOT_SENT: 'Not Sent',
  };

  return {
    id: proposal.id,
    title: proposal.title || '',
    documentNumber: proposal.documentNumber || '',
    version: proposal.version || '',
    clientName: proposal.clientName || '',
    stakeholder: proposal.sections?.pointOfContact || '',
    email: proposal.emailAddress || '',
    phone: [proposal.clientMobile, proposal.mobileNumber, proposal.phoneNumber, proposal.clientPhone].find((p) => p) || '',
    subtotal,
    taxAmount,
    total,
    depositAmount,
    depositPercent,
    currency: settings?.currency || 'AUD',
    financialItemDescriptions: items.map((i) => i.description || ''),
    financialItemAmounts: items.map((i) => Number(i.amount) || 0),
    publicationStatus,
    approvalStatus,
    approvalDetail: approvalDetailMap[approvalStatus] || 'Not Sent',
    updatedAt: proposal.updatedAt || 0,
  };
}

// ─── Ranking Engine ───────────────────────────────────────────────────

export function rankProposalSearchResults(proposals, settings, query) {
  if (!query || !query.trim()) {
    return proposals
      .map((p) => ({
        proposal: p,
        metadata: getProposalSearchMetadata(p, settings),
        score: 1,
        matchedFields: [],
      }))
      .sort((a, b) => b.metadata.updatedAt - a.metadata.updatedAt);
  }

  let cleanQuery = query.toLowerCase();
  const intents = {
    published: false,
    notPublished: false,
    approved: false,
    notApproved: false,
    pending: false,
    changesRequested: false,
    expired: false,
    revoked: false,
    sendFailed: false,
    notSent: false,
  };

  const extractIntent = (phrases, flag) => {
    for (const phrase of phrases) {
      if (cleanQuery.includes(phrase)) {
        intents[flag] = true;
        cleanQuery = cleanQuery.replace(new RegExp(phrase, 'g'), ' ');
      }
    }
  };

  extractIntent(['not published', 'unpublished', 'draft'], 'notPublished');
  extractIntent(['published'], 'published');
  extractIntent(['not approved', 'unapproved'], 'notApproved');
  extractIntent(['approved'], 'approved');
  extractIntent(['awaiting approval', 'pending'], 'pending');
  extractIntent(['changes requested'], 'changesRequested');
  extractIntent(['expired'], 'expired');
  extractIntent(['revoked'], 'revoked');
  extractIntent(['send failed'], 'sendFailed');
  extractIntent(['not sent'], 'notSent');

  cleanQuery = cleanQuery.replace(/\s+/g, ' ').trim();
  const tokens = cleanQuery.split(' ').filter((t) => t.length > 0);
  const parsedAmount = parseSearchAmount(cleanQuery);
  const hasPhoneToken = tokens.some((t) => t.replace(/\D/g, '').length >= 8);

  const results = [];

  for (const proposal of proposals) {
    const meta = getProposalSearchMetadata(proposal, settings);

    if (intents.published && meta.publicationStatus !== 'Published') continue;
    if (intents.notPublished && meta.publicationStatus === 'Published') continue;
    if (intents.approved && meta.approvalStatus !== 'APPROVED') continue;
    if (intents.notApproved && meta.approvalStatus === 'APPROVED') continue;
    if (intents.pending && meta.approvalStatus !== 'PENDING') continue;
    if (intents.changesRequested && meta.approvalStatus !== 'CHANGES_REQUESTED') continue;
    if (intents.expired && meta.approvalStatus !== 'EXPIRED') continue;
    if (intents.revoked && meta.approvalStatus !== 'REVOKED') continue;
    if (intents.sendFailed && meta.approvalStatus !== 'SEND_FAILED') continue;
    if (intents.notSent && meta.approvalStatus !== 'NOT_SENT') continue;

    if (
      tokens.length === 0 &&
      (intents.published ||
        intents.notPublished ||
        intents.approved ||
        intents.notApproved ||
        intents.pending ||
        intents.changesRequested ||
        intents.expired ||
        intents.revoked ||
        intents.sendFailed ||
        intents.notSent)
    ) {
      results.push({ proposal, metadata: meta, score: 100, matchedFields: ['Status filter'] });
      continue;
    }

    let totalScore = 0;
    const matchedLabels = new Set();

    const matchField = (text, weight, label) => {
      if (!text) return 0;
      const norm = normaliseSearchText(text);
      if (!norm) return 0;
      let bestScore = 0;

      if (norm === cleanQuery) bestScore = weight;
      else if (norm.includes(cleanQuery)) bestScore = weight * 0.9;

      let tokenMatches = 0;
      for (const token of tokens) {
        if (norm === token) tokenMatches += weight * 0.8;
        else if (norm.startsWith(token + ' ') || norm.includes(' ' + token + ' ')) tokenMatches += weight * 0.7;
        else if (norm.startsWith(token)) tokenMatches += weight * 0.6;
        else if (norm.includes(token)) tokenMatches += weight * 0.5;
        else {
          const subScore = subsequenceScore(token, norm);
          if (subScore > 0.8) tokenMatches += weight * 0.3 * subScore;
          else if (boundedDamerauLevenshtein(token, norm, 2) <= 2) tokenMatches += weight * 0.2;
        }
      }

      const finalFieldScore = Math.max(bestScore, tokenMatches);
      if (finalFieldScore > weight * 0.3) matchedLabels.add(label);
      return finalFieldScore;
    };

    const phoneDigits = normalisePhoneDigits(meta.phone);
    const matchPhone = () => {
      if (!phoneDigits) return 0;
      let pScore = 0;
      for (const token of tokens) {
        const tDigits = normalisePhoneDigits(token);
        if (tDigits.length >= 4 && phoneDigits.includes(tDigits)) {
          pScore += 110 * (tDigits.length / phoneDigits.length);
          matchedLabels.add('Phone');
        }
      }
      return pScore;
    };

    const matchAmount = (amt, weight, label) => {
      if (parsedAmount === null) return 0;
      if (Math.abs(amt - parsedAmount) < 0.01) {
        matchedLabels.add(label);
        return weight;
      }
      return 0;
    };

    totalScore += matchField(meta.title, 140, 'Title');
    totalScore += matchField(meta.documentNumber, 135, 'Document number');
    totalScore += matchField(meta.clientName, 115, 'Client');
    totalScore += matchField(meta.email, 110, 'Email');
    totalScore += matchPhone();
    totalScore += matchField(meta.stakeholder, 100, 'Stakeholder');
    totalScore += matchField(meta.version, 60, 'Version');

    if (parsedAmount !== null && !hasPhoneToken) {
      totalScore += matchAmount(meta.total, 130, 'Current amount payable');
      totalScore += matchAmount(meta.subtotal, 130, 'Subtotal');
      totalScore += matchAmount(meta.taxAmount, 130, 'GST');
      totalScore += matchAmount(meta.depositAmount, 130, 'Deposit');
      for (const a of meta.financialItemAmounts) {
        totalScore += matchAmount(a, 130, 'Line item');
      }
    }

    for (const desc of meta.financialItemDescriptions) {
      totalScore += matchField(desc, 70, 'Line item');
    }

    if (totalScore > 30) {
      results.push({
        proposal,
        metadata: meta,
        score: totalScore,
        matchedFields: Array.from(matchedLabels).slice(0, 3),
      });
    }
  }

  return results.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return b.metadata.updatedAt - a.metadata.updatedAt;
  });
}

// ─── Component ────────────────────────────────────────────────────────

function normaliseTelHref(phone) {
  if (!phone) return '';
  let cleaned = phone.replace(/[\s()\-\u2010\u2011\u2012\u2013\u2014]/g, '');
  if (cleaned.startsWith('+61') && /^\+61\d+$/.test(cleaned)) return cleaned;
  if (cleaned.startsWith('04') && /^\d+$/.test(cleaned.slice(2))) return '+614' + cleaned.slice(2);
  if (cleaned.startsWith('614') && /^\d+$/.test(cleaned.slice(3))) return '+614' + cleaned.slice(3);
  return '';
}

export default function ProposalCommandPalette({ open, onClose }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const inputRef = useRef(null);
  const listboxRef = useRef(null);

  const rawProposals = useLiveQuery(() => db.proposals.orderBy('updatedAt').reverse().toArray()) || [];
  const settings = useLiveQuery(() => db.settings.get('global')) || {};

  const searchResults = useMemo(() => {
    const ranked = rankProposalSearchResults(rawProposals, settings, deferredQuery);
    return deferredQuery.trim() ? ranked.slice(0, 20) : ranked.slice(0, 8);
  }, [rawProposals, settings, deferredQuery]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
      document.body.style.overflow = 'hidden';
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [deferredQuery]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1 < searchResults.length ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (searchResults.length > 0 && searchResults[selectedIndex]) {
        navigate(`/proposals/${searchResults[selectedIndex].proposal.id}`);
        onClose();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  useEffect(() => {
    if (listboxRef.current && searchResults.length > 0) {
      const activeEl = listboxRef.current.querySelector('[aria-selected="true"]');
      if (activeEl) {
        activeEl.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex, searchResults]);

  if (!open) return null;

  return (
    <AnimatePresence>
      <div
        id="proposal-command-palette"
        role="dialog"
        aria-modal="true"
        aria-label="Search proposals"
        className="fixed inset-0 z-[500] flex items-start justify-center pt-[10dvh] sm:pt-[15dvh] p-4"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
          onClick={onClose}
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: -10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -10 }}
          transition={{ duration: 0.2 }}
          className="relative w-full max-w-[760px] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col border border-gray-200"
        >
          <div className="relative flex items-center px-4 border-b border-gray-100 bg-white">
            <SafeIcon icon={FiIcons.FiSearch} className="text-xl text-gray-400 shrink-0 ml-2" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search title, phone, email, amount or status…"
              className="flex-1 w-full py-5 px-4 text-lg font-medium text-gray-900 placeholder-gray-400 outline-none bg-transparent"
              aria-expanded="true"
              aria-controls="proposal-search-results"
              aria-activedescendant={searchResults.length > 0 ? `result-${selectedIndex}` : undefined}
            />
            {query && (
              <button
                onClick={() => {
                  setQuery('');
                  inputRef.current?.focus();
                }}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors shrink-0"
                aria-label="Clear search"
              >
                <SafeIcon icon={FiIcons.FiX} className="text-lg" />
              </button>
            )}
            <div className="hidden sm:flex items-center gap-1.5 ml-2 pl-4 border-l border-gray-100">
              <span className="text-[10px] font-bold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">↑↓</span>
              <span className="text-[10px] font-bold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">Enter</span>
              <span className="text-[10px] font-bold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded ml-1">Esc</span>
            </div>
          </div>

          <div
            ref={listboxRef}
            id="proposal-search-results"
            role="listbox"
            className="max-h-[60dvh] overflow-y-auto bg-gray-50/50 p-2"
          >
            {rawProposals.length === 0 ? (
              <div className="py-16 px-6 text-center">
                <SafeIcon icon={FiIcons.FiInbox} className="text-4xl text-gray-300 mx-auto mb-3" />
                <p className="text-sm font-bold text-gray-900">No proposals are available yet.</p>
                <p className="text-xs text-gray-500 mt-1">Create a new Statement of Work to get started.</p>
              </div>
            ) : searchResults.length === 0 ? (
              <div className="py-16 px-6 text-center">
                <SafeIcon icon={FiIcons.FiSearch} className="text-4xl text-gray-300 mx-auto mb-3" />
                <p className="text-sm font-bold text-gray-900">No matching proposals</p>
                <p className="text-xs text-gray-500 mt-1">Try a client, email, phone number, amount, SOW number or status.</p>
              </div>
            ) : (
              <>
                <div className="px-3 py-2 text-[10px] font-black uppercase tracking-widest text-gray-400">
                  {!deferredQuery.trim() ? 'Recent proposals' : 'Search results'}
                </div>
                {searchResults.map((result, index) => {
                  const { proposal, metadata, matchedFields } = result;
                  const isSelected = index === selectedIndex;
                  const currencySym = normaliseCurrencyText(metadata.currency) === 'USD' ? '$' : metadata.currency === 'EUR' ? '€' : metadata.currency === 'GBP' ? '£' : '$';
                  const formattedTotal = new Intl.NumberFormat('en-AU', {
                    style: 'currency',
                    currency: metadata.currency || 'AUD',
                  }).format(metadata.total);

                  const phoneHref = normaliseTelHref(metadata.phone);
                  const isPub = metadata.publicationStatus === 'Published';
                  const isApp = metadata.approvalStatus === 'APPROVED';

                  return (
                    <div
                      key={proposal.id}
                      id={`result-${index}`}
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => {
                        navigate(`/proposals/${proposal.id}`);
                        onClose();
                      }}
                      onMouseMove={() => setSelectedIndex(index)}
                      className={`block w-full text-left p-4 mb-2 rounded-xl border cursor-pointer transition-all ${
                        isSelected
                          ? 'bg-indigo-600 border-indigo-700 shadow-md transform scale-[1.01]'
                          : 'bg-white border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2 gap-4">
                        <div className="min-w-0 flex-1">
                          <h3
                            className={`text-sm font-bold truncate ${
                              isSelected ? 'text-white' : 'text-gray-900'
                            }`}
                          >
                            {metadata.title || 'Untitled Proposal'}
                          </h3>
                          <div
                            className={`flex items-center gap-2 mt-1 text-[10px] font-bold uppercase tracking-widest ${
                              isSelected ? 'text-indigo-200' : 'text-gray-400'
                            }`}
                          >
                            {metadata.documentNumber || 'Pending'}
                            {metadata.version && ` · v${metadata.version}`}
                            <span className="normal-case tracking-normal font-medium">
                              · Modified {new Date(metadata.updatedAt).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        {metadata.total > 0 && (
                          <div
                            className={`text-sm font-black whitespace-nowrap shrink-0 ${
                              isSelected ? 'text-white' : 'text-gray-900'
                            }`}
                          >
                            {formattedTotal}
                          </div>
                        )}
                      </div>

                      {(metadata.clientName || metadata.stakeholder || metadata.email || metadata.phone) && (
                        <div
                          className={`flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs font-medium ${
                            isSelected ? 'text-indigo-100' : 'text-gray-600'
                          }`}
                        >
                          {metadata.clientName && (
                            <span className="flex items-center gap-1.5">
                              <SafeIcon icon={FiIcons.FiBriefcase} /> {metadata.clientName}
                            </span>
                          )}
                          {metadata.stakeholder && (
                            <span className="flex items-center gap-1.5">
                              <SafeIcon icon={FiIcons.FiUser} /> {metadata.stakeholder}
                            </span>
                          )}
                          {metadata.email && (
                            <a
                              href={`mailto:${metadata.email}`}
                              onClick={(e) => e.stopPropagation()}
                              className={`flex items-center gap-1.5 hover:underline ${
                                isSelected ? 'text-white' : 'text-indigo-600'
                              }`}
                            >
                              <SafeIcon icon={FiIcons.FiMail} /> {metadata.email}
                            </a>
                          )}
                          {metadata.phone && phoneHref && (
                            <a
                              href={`tel:${phoneHref}`}
                              onClick={(e) => e.stopPropagation()}
                              className={`flex items-center gap-1.5 hover:underline ${
                                isSelected ? 'text-white' : 'text-indigo-600'
                              }`}
                            >
                              <SafeIcon icon={FiIcons.FiPhone} /> {metadata.phone}
                            </a>
                          )}
                        </div>
                      )}

                      <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-3 border-t border-opacity-20 border-current">
                        <div className="flex flex-wrap items-center gap-2">
                          <span
                            className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest ${
                              isSelected
                                ? isPub
                                  ? 'bg-white/20 text-white'
                                  : 'bg-indigo-800 text-indigo-200'
                                : isPub
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {metadata.publicationStatus}
                          </span>
                          <span
                            className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest ${
                              isSelected
                                ? isApp
                                  ? 'bg-white/20 text-white'
                                  : 'bg-indigo-800 text-indigo-200'
                                : isApp
                                ? 'bg-green-100 text-green-800'
                                : 'bg-amber-100 text-amber-800'
                            }`}
                          >
                            {isApp ? 'Approved' : `Not Approved · ${metadata.approvalDetail}`}
                          </span>
                        </div>
                        {matchedFields.length > 0 && deferredQuery.trim() && (
                          <div
                            className={`text-[9px] font-bold uppercase tracking-widest ${
                              isSelected ? 'text-indigo-300' : 'text-gray-400'
                            }`}
                          >
                            Matched on: {matchedFields.join(', ')}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}